import { HarmReport, InterventionDecision, UserType } from '@inplay/models';

export type SectionPermission = {
  editable: boolean;
};

export type HarmReportEditPermissions = {
  canEdit: boolean;
  sections: {
    topSection: SectionPermission;
    notesAndObservations: SectionPermission;
    managersReview: SectionPermission;
    comments: SectionPermission;
  };
};

export const INTERVENTION_DURATION_MS = 20 * 60 * 1000; // 20 minutes (default)
export const MANAGERS_REVIEW_DURATION_MS = 10 * 60 * 1000; // 10 minutes (default)

const isStaffRole = (userType: UserType | undefined): boolean =>
  userType === UserType.VENUE_STAFF;

const isAutoGenerated = (report: HarmReport): boolean =>
  report.identifiedBy?.type === 'System';

const is1stInstance = (report: HarmReport): boolean =>
  report.harmInstanceCountForPlayer === '1st';

const isReviewComplete = (report: HarmReport): boolean => !!report.reviewedBy;

const hasLinkedIR = (report: HarmReport): boolean => !!report.intervention?.id;

const isInterventionNotRequired = (report: HarmReport): boolean =>
  report.intervention?.decision === InterventionDecision.NOT_REQUIRED;

const isWithinWindow = (fromDate: Date, windowMs: number): boolean =>
  Date.now() - fromDate.getTime() < windowMs;

const LOCKED: HarmReportEditPermissions = {
  canEdit: false,
  sections: {
    topSection: { editable: false },
    notesAndObservations: { editable: false },
    managersReview: { editable: false },
    comments: { editable: false },
  },
};

const FULLY_EDITABLE: HarmReportEditPermissions = {
  canEdit: true,
  sections: {
    topSection: { editable: true },
    notesAndObservations: { editable: true },
    managersReview: { editable: true },
    comments: { editable: true },
  },
};

// Auto-generated: admins can only edit Name, Description & Notes
const AUTO_ADMIN_LIMITED: HarmReportEditPermissions = {
  canEdit: true,
  sections: {
    topSection: { editable: true }, // Name & Description are in top section
    notesAndObservations: { editable: true },
    managersReview: { editable: false },
    comments: { editable: false },
  },
};

// Manager's review section only
const MANAGERS_REVIEW_ONLY: HarmReportEditPermissions = {
  canEdit: true,
  sections: {
    topSection: { editable: false },
    notesAndObservations: { editable: false },
    managersReview: { editable: true },
    comments: { editable: true },
  },
};

// Comments field only (in manager's review section)
const COMMENTS_ONLY: HarmReportEditPermissions = {
  canEdit: true,
  sections: {
    topSection: { editable: false },
    notesAndObservations: { editable: false },
    managersReview: { editable: false },
    comments: { editable: true },
  },
};

/**
 * Determines if the current user is the report creator (staff only distinction).
 */
const isReportCreator = (report: HarmReport, currentUserId?: string): boolean =>
  !!currentUserId && report.createdBy?.userId === currentUserId;

/**
 * Computes edit permissions for a harm report.
 *
 * Rules based on:
 * - Report origin (manual vs auto-generated)
 * - Instance (1st vs 2nd+)
 * - User role (staff vs admin — all admins equal)
 * - Time windows (20m intervention, 10m manager's review)
 * - Whether IR has been created
 * - Whether intervention is required
 */
export type EditPermissionsOptions = {
  interventionDurationMs?: number;
  managersReviewDurationMs?: number;
};

export const getHarmReportEditPermissions = (
  report: HarmReport,
  userType: UserType | undefined,
  currentUserId?: string,
  options?: EditPermissionsOptions,
): HarmReportEditPermissions => {
  const interventionDurationMs =
    options?.interventionDurationMs ?? INTERVENTION_DURATION_MS;
  const managersReviewDurationMs =
    options?.managersReviewDurationMs ?? MANAGERS_REVIEW_DURATION_MS;
  const auto = isAutoGenerated(report);
  const first = is1stInstance(report);
  const staff = isStaffRole(userType);
  const createdAt = new Date(report.createdAt);
  const irCreated = hasLinkedIR(report);

  // ═══════════════════════════════════════════════
  // 1ST INSTANCE
  // ═══════════════════════════════════════════════
  if (first) {
    const interventionWindowActive = isWithinWindow(
      createdAt,
      interventionDurationMs,
    );

    // Scenario 03/04: After 20m expires OR IR created → fully locked
    if (!interventionWindowActive || irCreated) {
      return LOCKED;
    }

    // Scenario 01/02: Within intervention window and no IR yet
    if (auto) {
      // Auto: staff locked, admins get Name/Description/Notes only
      if (staff) return LOCKED;
      return AUTO_ADMIN_LIMITED;
    }

    // Manual: staff report creator can edit, other staff locked, all admins can edit
    if (staff) {
      return isReportCreator(report, currentUserId) ? FULLY_EDITABLE : LOCKED;
    }
    return FULLY_EDITABLE;
  }

  // ═══════════════════════════════════════════════
  // 2ND+ INSTANCE
  // ═══════════════════════════════════════════════
  const reviewDurationActive = isWithinWindow(
    createdAt,
    managersReviewDurationMs,
  );
  const reviewed = isReviewComplete(report);

  // --- Before review is complete ---
  if (!reviewed) {
    // Scenario 05: 10m manager's review duration expired without review → notes only (admins), locked (staff)
    if (!reviewDurationActive) {
      if (staff) return LOCKED;
      return COMMENTS_ONLY;
    }

    // Scenario 01/02: Within review window, not yet reviewed
    if (auto) {
      if (staff) return LOCKED;
      return AUTO_ADMIN_LIMITED;
    }

    // Manual: staff creator can edit, other staff locked, admins full edit
    if (staff) {
      return isReportCreator(report, currentUserId) ? FULLY_EDITABLE : LOCKED;
    }
    return FULLY_EDITABLE;
  }

  // --- After review is complete ---
  // Staff: always locked after review
  if (staff) return LOCKED;

  const reviewedAt = report.reviewedBy?.signedDate
    ? new Date(report.reviewedBy.signedDate)
    : createdAt;

  // Scenario 06: Intervention not required → manager's review section till 10m ends, then comments only
  if (isInterventionNotRequired(report)) {
    if (reviewDurationActive) {
      return MANAGERS_REVIEW_ONLY;
    }
    return COMMENTS_ONLY;
  }

  // Scenario 07/08: 20m intervention window (from review time) expires OR IR created → fully locked
  const interventionWindowActive = isWithinWindow(
    reviewedAt,
    interventionDurationMs,
  );
  if (!interventionWindowActive || irCreated) {
    return LOCKED;
  }

  // Scenario 03/04: Reviewed, within 20m intervention window, IR not yet created
  return MANAGERS_REVIEW_ONLY;
};
